import useAppSelector from '@/features/app/hooks/useAppSelector';
import { EnhancedUseQueryOptions } from '@/shared/types/enhanced-use-query-options';
import { useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';

/**
 * Wrapper around `useQuery` hook to provide extra fields such as `queryKey` and `invalidate` function.
 * @param options - The options for the query.
 * @returns The enhanced query result.
 */
export default function useAuthenticatedQuery<T>(
  options: ({ isEnhancedUseQueryOptions?: false } & UseQueryOptions<T>) | EnhancedUseQueryOptions<T>
) {
  const queryClient = useQueryClient();
  const { isAccessTokenStillValid, user } = useAppSelector((state) => state.auth);
  const queryOptions = useMemo(
    () => (options.isEnhancedUseQueryOptions ? options.queryOptions : options),
    [options]
  );

  const queryResult = useQuery({
    ...queryOptions,
    enabled: !!user && isAccessTokenStillValid && (queryOptions?.enabled ?? true),
  });

  return {
    queryKey: queryOptions.queryKey,
    invalidate: () =>
      queryClient.invalidateQueries({
        queryKey: queryOptions.queryKey,
      }),
    queryOptions: options,
    ...queryResult,
  };
}
